<template>
    <div class="container h-full">
        <div class="overflow-hidden rounded-md">
            <div class="px-4 py-5 space-y-6 bg-white">
                <div class="flex flex-col lg:flex-row">
                    <!-- Category Image -->
                    <div class="relative group">
                        <!-- image render -->
                        <div
                            class="w-full md:w-40 h-40 px-4 lg:px-0 mb-2"
                            @click="replaceImage('image')"
                        >
                            <!-- if editData had image and new one was selected then this is that new image's preview -->
                            <img
                                v-if="imagePreview"
                                class="object-cover object-center max-w-full max-h-full rounded h-36 w-36"
                                :src="imagePreview"
                            />
                            <!-- if editData has image then this is the image that is rendered at first on edit view -->
                            <img
                                v-if="!imagePreview && editData && editData.images.length"
                                class="object-cover object-center max-w-full max-h-full rounded h-36 w-36"
                                :src="editData.images[0].image_path"
                                :alt="editData.name"
                            />
                            <!-- if this is create form or there is no category image then this is shown -->
                            <div
                                v-if="!imagePreview && (!editData || (editData && !editData.images.length))"
                                class="relative font-medium text-green-600 bg-white rounded-md cursor-pointer hover:text-green-500"
                            >
                                <div
                                    ref="fileUpload"
                                    class="flex justify-center px-6 pt-5 pb-6 mt-2 border-2 border-gray-300 border-dashed rounded-md"
                                >
                                    <div class="flex flex-col items-center justify-center space-y-1 text-center">
                                        <FileUploadIcon />
                                        <div class="flex items-center justify-center text-sm text-gray-600">
                                            <span>Upload an image</span>
                                        </div>
                                        <p class="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- image input -->
                        <input
                            ref="category-image-upload"
                            class="hidden"
                            type="file"
                            @change="filesChange($event.target.files)"
                        />
                    </div>
                    <!-- Category Form -->
                    <div class="flex-grow ml-4 space-y-4">
                        <!-- Category name -->
                        <div>
                            <div class="block text-sm font-medium text-gray-700">Category*</div>

                            <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                <t-input
                                    v-model="form.name"
                                    placeholder="Category Name"
                                    :variant="errors('name') ? 'danger' : ''"
                                />
                                <div
                                    v-if="errors('name')"
                                    class="text-red-500"
                                >
                                    {{ errors('name') }}
                                </div>
                            </div>
                        </div>
                        <!-- Category parent -->
                        <div>
                            <div class="block text-sm font-medium text-gray-700">Category Parent</div>

                            <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                <t-select
                                    v-model="form.parentId"
                                    placeholder="No Parent Category"
                                    :options="categoriesListExceptSelf"
                                    text-attribute="name"
                                    value-attribute="id"
                                    :variant="errors('parent_id') ? 'danger' : ''"
                                />
                                <div
                                    v-if="errors('parent_id')"
                                    class="text-red-500"
                                >
                                    {{ errors('parent_id') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Banner Image -->
                <div class="relative w-full group p-2">
                    <div class="block mb-2 text-sm font-medium text-gray-700">Banner Image*</div>
                    <div
                        class="h-48 w-full"
                        @click="replaceImage('banner')"
                    >
                        <!-- if editData had banner and new one was selected then this is that new banner's preview -->
                        <img
                            v-if="bannerPreview"
                            class="object-cover object-center max-w-full max-h-full rounded"
                            :src="bannerPreview"
                        />

                        <!-- if editData has banner then this is the banner that is rendered at first on edit view -->
                        <img
                            v-if="!bannerPreview && editData && editData.banner_path"
                            class="object-cover object-center max-w-full max-h-full rounded"
                            :src="editData.banner_path"
                            :alt="editData.name"
                        />

                        <!-- if this is create form or there is no category banner then this is shown -->

                        <div
                            v-if="!bannerPreview && (!editData || (editData && !editData.banner_path))"
                            class="relative font-medium text-green-600 bg-white rounded-md cursor-pointer hover:text-green-500"
                        >
                            <div
                                ref="fileUpload"
                                class="flex justify-center px-6 pt-5 pb-6 mt-2 border-2 border-gray-300 border-dashed rounded-md"
                                :class="errors('banner') ? 'border-red-300' : 'border-gray-300'"
                            >
                                <div class="flex flex-col items-center justify-center space-y-1 text-center">
                                    <FileUploadIcon />
                                    <div class="flex items-center justify-center text-sm text-gray-600">
                                        <span>Upload Banner Image</span>
                                    </div>
                                    <p class="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="errors('name')"
                            class="text-red-500"
                        >
                            {{ errors('banner') }}
                        </div>
                    </div>

                    <input
                        ref="category-banner-upload"
                        class="hidden"
                        type="file"
                        @change="filesChange($event.target.files, 'banner')"
                    />
                </div>
            </div>

            <div class="px-4 py-3 text-right bg-gray-50">
                <t-button
                    variant="success"
                    :disabled="processing"
                    size="sm"
                    @click="submitForm"
                >
                    {{ processing ? 'Saving...' : 'Save' }}
                </t-button>
            </div>
        </div>
    </div>
</template>

<script>
    import Category from '../Models/Category';
    import FileUploadIcon from './Icons/FileUploadIcon.vue';

    export default {
        name: 'CategoriesCreate',

        components: {
            FileUploadIcon,
        },

        props: {
            editData: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                form: new Category(),
                categoriesList: [],
                imagePreview: null,
                bannerPreview: null,
                processing: false,
            };
        },

        computed: {
            categoriesListExceptSelf() {
                if (this.categoriesList.length === 0) {
                    return [];
                }
                if (this.editData) {
                    return this.categoriesList.filter(
                        (category) => category.id !== this.editData.id && category.parent_id === null,
                    );
                }
                return this.categoriesList.filter((category) => category.parent_id === null);

                // if (this.editData) {
                //     if (this.categoriesList.data.length === 0) {
                //         return [];
                //     }
                //     return this.categoriesList.filter(
                //         (category) => category.id !== this.editData.id && category.parent_id === null
                //     );
                // }
                // return this.categoriesList.data;
            },
        },

        async mounted() {
            this.fetchCategories();
            if (this.editData) {
                this.form.name = this.editData.name;
                this.form.parentId = this.editData.parent_id;
            }
        },

        methods: {
            submitForm() {
                const payload = this.buildPayload();
                this.editData ? this.update(payload) : this.save(payload);
            },

            buildPayload() {
                const formData = new FormData();
                if (this.form.name) {
                    formData.append('name', this.form.name);
                }
                if (this.form.parentId) {
                    formData.append('parent_id', this.form.parentId);
                }
                if (this.form.image) {
                    formData.append('image', this.form.image);
                }
                if (this.form.banner) {
                    formData.append('banner', this.form.banner);
                }
                if (this.editData) {
                    formData.append('_method', 'PUT');
                }
                return formData;
            },

            save(payload) {
                this.processing = true;
                this.$inertia.post(route('categories.store'), payload, {
                    onSuccess: () => {
                        this.$emit('transactionComplete');
                    },
                    onFinish: () => {
                        this.processing = false;
                    },
                });
            },

            update(payload) {
                this.processing = true;
                this.$inertia.post(route('categories.update', this.editData.id), payload, {
                    onSuccess: () => {
                        this.$emit('transactionComplete');
                    },
                    onFinish: () => {
                        this.processing = false;
                    },
                });
            },

            replaceImage(fileType) {
                this.$refs[`category-${fileType}-upload`].click();
            },

            filesChange(fileList, fileType = 'image') {
                if (!fileList.length) return;
                if (fileType === 'banner') {
                    this.form.banner = fileList[0];
                    this.getBannerPreviews();
                } else if (fileType === 'image') {
                    this.form.image = fileList[0];
                    this.getImagePreviews();
                }
            },

            deleteImage(image) {
                this.$inertia.delete(route('images.delete', image.id));
            },

            getImagePreviews() {
                if (/\.(jpe?g|png|gif)$/i.test(this.form.image.name)) {
                    const reader = new FileReader();
                    reader.addEventListener(
                        'load',
                        () => {
                            this.imagePreview = reader.result;
                        },
                        false,
                    );

                    reader.readAsDataURL(this.form.image);
                }
            },

            getBannerPreviews() {
                if (/\.(jpe?g|png|gif)$/i.test(this.form.banner.name)) {
                    const reader = new FileReader();
                    reader.addEventListener(
                        'load',
                        () => {
                            this.bannerPreview = reader.result;
                        },
                        false,
                    );

                    reader.readAsDataURL(this.form.banner);
                }
            },

            fetchCategories() {
                this.$axios.get(route('categories.index')).then((res) => {
                    this.categoriesList = res.data.allCategories;
                });
            },
        },
    };
</script>
